import { keyBindingFor } from "../transitions.js";
import { MDY_WIDGET_CONTRACTS } from "../catalog/contracts.js";
/**
 * Whether a variant of this kind draws the named part.
 *
 * Read from the variant's own `required` list, which is where the difference between the two modes
 * is already stated. A policy that named the mode instead would be a second statement of it.
 */
function variantRequires(mode, part) {
    return MDY_WIDGET_CONTRACTS.multiselect.variants[mode]?.required?.includes(part) === true;
}
/** Pure multiselect value transition using the same loose key semantics as select. */
export function multiselectValueTransition(values, intent, keyFor = String) {
    if (intent.type === "clear")
        return [];
    const key = keyFor(intent.value);
    if (intent.type === "increment")
        return [...values, intent.value];
    const index = values.findIndex((value) => keyFor(value) === key);
    if (intent.type === "decrement") {
        if (index < 0)
            return values;
        return [...values.slice(0, index), ...values.slice(index + 1)];
    }
    return index < 0
        ? [...values, intent.value]
        : values.filter((value) => keyFor(value) !== key);
}
/** Canonical multiselect overlay policy. The host only supplies event facts and executes the action. */
export function multiselectOverlayAction(input) {
    const { open, activeKey } = input;
    // A press with the platform's accelerator held is the platform's — `Cmd+Space` switches the input
    // source, `Cmd+ArrowDown` reaches the end of a document — so a gesture that *adds* something does
    // not answer it. `Escape` does, and that is the whole of the distinction: answering a dismissal
    // wrongly costs a reopen, refusing one leaves somebody inside a panel with the way out not
    // working. A caller passing a bare key name is asking what the kind declares, and gets that.
    const pressed = typeof input.key === "string" ? null : input.key;
    const key = typeof input.key === "string" ? input.key : input.key.key;
    // Which gestures survive a held accelerator is read from the catalogue, never named here. The
    // binding carries it: absent means bare, so opening and committing refuse a press that may have
    // been aimed at the platform; `"any"` is what a dismissal declares, because refusing one leaves
    // somebody inside a panel with the way out shut. Naming `Escape` in this line instead would be a
    // second copy of that rule, and the copy is what stops moving when the declaration does.
    if (pressed !== null && (pressed.ctrlKey === true || pressed.metaKey === true)
        && keyBindingFor("multiselect", pressed, open) === null) {
        return null;
    }
    if (key === "Escape" && open)
        return { type: "close", restoreFocus: true };
    if (key === "Enter") {
        if (!open)
            return { type: "open" };
        return activeKey ? { type: "select", optionKey: activeKey } : null;
    }
    const moves = {
        ArrowDown: "next",
        ArrowUp: "previous",
        Home: "first",
        End: "last",
    };
    const target = moves[key];
    if (target) {
        // A closed list has nothing to move through, so a vertical arrow reaches for the options and
        // opens it — either one. `MDY_WIDGET_KEYBOARD` declares both, and a single-select combobox
        // answers both; a policy that opened on one of them made the same widget behave two ways
        // depending on which key a user happened to press, and disagreed with the table describing it.
        //
        // `Home` and `End` still do nothing here: they mean "the first" and "the last" of a list that is
        // not on screen, and opening on them would be inventing an intent the contract does not declare.
        if (!open)
            return key === "ArrowDown" || key === "ArrowUp" ? { type: "open" } : null;
        return { type: "move", target };
    }
    // The quantity on the option the cursor is on.
    //
    // Which keys, and which way each one goes, are read from the kind's declaration rather than named
    // here: the pair is `on: "option"` with `intent: "step"`, and a second copy of the key names in
    // this file is the copy that stops moving when the declaration does.
    //
    // Whether the option carries a quantity at all is read from the same place — `optionStep` is
    // required by one variant and absent from the other, so the mode's own declaration decides, and no
    // clause here has to be remembered when a variant changes.
    const step = open && activeKey !== null && input.mode !== undefined
        ? keyBindingFor("multiselect", input.key, open, "option")
        : null;
    if (step?.intent === "step" && step.by !== undefined
        && variantRequires(input.mode, "optionStep")) {
        return { type: "step", optionKey: activeKey, by: step.by };
    }
    // Tab closes and lets focus go where it was headed. A list left open follows the user to the next
    // field, and focus pulled back traps them in the one they just left.
    if (key === "Tab" && open)
        return { type: "close", restoreFocus: false };
    // Backspace answers nothing here.
    //
    // It used to return "clear the search" when the search was *already empty* — an action that changes
    // nothing, handed to a caller that prevents the default because it was given an action. All three
    // renderers therefore swallowed the key at the trigger and did nothing with it, including with the
    // panel closed, where there is no search box to clear.
    //
    // The key is declared on the chip, where it takes a chosen value off. Claimed at the control it was
    // taken away from the person and given to nobody: a key that is prevented and unanswered is worse
    // than one nothing claims, because the platform's own meaning is gone too.
    return null;
}
/** Single-mode closes only when no unselected result remains after the commit. */
export function shouldCloseMultiselectOverlay(mode, remainingResultCount) {
    return mode === "single" && remainingResultCount === 0;
}
/** ISO date bound policy shared by typed input, calendar selection and future hosts. */
